import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Inject, Input, OnChanges, Output } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

import { CurrencyPipeInput } from '@frontend/sports/common/core/feature/money';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';

import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';

export interface ITaxationInfoViewModel {
    possibleWinningsGross: Decimal | null;
    possibleWinningsNet: Nullable<Decimal> | undefined;
    taxationAmount: Decimal | null;
    taxationRate: Decimal | null;
    winningsBoost?: Decimal | null;
    taxationLiabilityTitle?: string;
    taxationLiability?: CurrencyPipeInput;
    taxationMessage?: string | null | undefined;
}

@Component({
    selector: 'bs-digital-taxation-info',
    templateUrl: './taxation-info.component.html',
    styleUrls: ['./taxation-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class TaxationInfoComponent implements OnChanges {
    @Input() vm: ITaxationInfoViewModel;
    @Input() isVisible = false;
    @Output() isVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    private wasInfoShown = false;
    readonly prependCurrencySymbol: boolean;

    readonly messages: {
        possibleWinnings: string;
        possibleWinningsNet: string;
        notBoostedPossibleWinnings: string;
        boostedPossibleWinnings: string;
        taxationHelpUrl?: string;
        closeButton: string;
        taxationHelpText: SafeHtml;
        taxationNoRate: string;
        taxationRate: string;
    };

    constructor(
        private userSettings: SyncUserSettingsService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private stringInterpolation: StringInterpolationPipe,
    ) {
        this.messages = messageProvider.getMessages().taxationInfo;
    }

    get isBoosted(): boolean {
        return !!this.vm.winningsBoost;
    }

    get grossNonBoostedWinningsMessage(): string {
        return this.isBoosted ? this.messages.notBoostedPossibleWinnings : this.messages.possibleWinnings;
    }

    get taxationTitle(): string {
        if (!this.vm.taxationRate?.greaterThan(0)) {
            return this.messages.taxationNoRate;
        } else {
            return this.stringInterpolation.transform(this.messages.taxationRate, { tax: this.vm.taxationRate.times(100).toNumber() });
        }
    }

    get isTurnoverTax(): boolean {
        return this.userSettings.isTurnoverTaxApplied;
    }

    close(): void {
        this.wasInfoShown = false;
        this.isVisibleChange.emit(false);
    }

    ngOnChanges(changes: ISimpleChanges<TaxationInfoComponent>): void {
        if (changes.isVisible && changes.isVisible.currentValue && !this.wasInfoShown) {
            this.wasInfoShown = true;
        }
    }

    @HostListener('document:click', ['$event'])
    clickout(): void {
        if (!this.wasInfoShown) {
            return;
        }

        this.close();
    }

    @HostListener('click', ['$event'])
    onClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }
}
